<template>
  <div :class="['device-selection virtual-bg', { loading: loading }]">
    <div v-for="(item, idx) in bgOptions" :key="idx" :class="{ disabled : disabledSelection || tempDisabled }">
      <div :class="['icon', { active: virtualBg === item.value }]" @click="selectVirtualBg(item.value)">
        <img :src="item.icon" />
      </div>
    </div>
    <!-- Shows video with virtual background -->
    <canvas class="virtual-bg-video"></canvas>
    <!-- Original video to be processed for blur effect -->
    <video class="for-process-video" muted="muted" autoplay playsinline />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { store } from '@/utils/common';

import virtualBgMixin from '@/mixins/virtualBg';

export default {
  mixins: [virtualBgMixin],
  data() {
    return {
      loading: false,
      webgl2Support: true,
      hardwareAcceleration: true
    };
  },
  created() {
    const videoEffect = store.getBlurState();
    this.setVirtualBg(videoEffect);
  },
  mounted() {
    // webgl v2 is recommended for smooth blur effect
    this.webgl2Support = !!document.createElement('canvas').getContext('webgl2');
  },
  watch: {
    isLessonEnded(state) {
      if (state && this.virtualBgModel) {
        this.virtualBgModel.releaseMemory();
      }
    },
    disabledSelection(val) {
      if (val) {
        this.logBlurDisabledReason();
        this.setVirtualBg('none');
        store.setBlurState('none');
      }
    }
  },
  computed: {
    ...mapState(['lessonId']),
    ...mapState('videoRtc', ['rtcClient', 'localUser', 'virtualBg', 'virtualBgModel', 'isShareScreenOn']),
    ...mapGetters(['isLessonEnded', 'isWaitingForLesson']),

    tempDisabled() {
      return this.isLessonEnded || this.isShareScreenOn
    },

    disabledSelection() {
      return (
        !this.webgl2Support ||
        !this.hardwareAcceleration ||
        this.noCamera
      );
    },

    noCamera() {
      return this.localUser.stream && !this.localUser.stream.getVideoTracks().length;
    },

    bgOptions() {
      return [
        {
          icon: require('@/assets/img/blur_icn-off.svg'),
          value: 'none'
        },
        {
          icon: require('@/assets/img/blur_icn-on.svg'),
          value: 'blur'
        }
      ];
    }
  },
  methods: {
    ...mapMutations('videoRtc', ['setVirtualBg', 'setVirtualBgModel']),
    ...mapActions('videoRtc', ['updateLocalStream']),

    selectVirtualBg(newEffect) {
      if (this.virtualBg === newEffect || this.disabledSelection || this.tempDisabled) {
        return;
      }

      if (this.isWaitingForLesson) {
        this.setVirtualBg(newEffect);
        store.setBlurState(newEffect);
        return;
      }

      this.applyEffect(newEffect);
    },

    ignoreBackgroundEffect() {
      return this.loading || !this.localUser.stream;
    },

    async applyEffect(effect) {
      if (this.ignoreBackgroundEffect()) {
        return;
      }
      this.loading = true;
      this.hardwareAcceleration = await this.loadVBModel();

      if (!this.hardwareAcceleration) {
        this.loading = false;
        return;
      }

      // Perform Video Process and Publish Effect to Stream
      const rtc = await this.rtcClient.getAdapter().setVideoEffect(effect, this.virtualBgModel);
      this.updateLocalStream(rtc);

      this.setVirtualBg(effect);
      store.setBlurState(effect);

      // add delay to prevent republishing stream immediately
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    },

    logBlurDisabledReason() {
      let reason;
      if (!this.webgl2Support) {
        reason = 'WebGL is not supported';
      } else if (this.isShareScreenOn) {
        reason = 'Share screen is currently active';
      } else if (!this.hardwareAcceleration) {
        reason = 'Hardware Acceleration is disabled';
      } else if (this.noCamera) {
        reason = 'Cannot access camera';
      }
      this.$logger.info('Blur is disabled', { operation: 'blur:disabled', reason: reason });
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/side_menu.css"></style>
